import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import * as React from "react";

const messageVariants = cva("flex items-start gap-3 rounded-lg border p-4 text-sm", {
  variants: {
    variant: {
      success:
        "border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-100",
      error:
        "border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100",
      warning:
        "border-yellow-200 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-100",
      info: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const iconVariants = cva("h-5 w-5 flex-shrink-0", {
  variants: {
    variant: {
      success: "text-green-600 dark:text-green-400",
      error: "text-red-600 dark:text-red-400",
      warning: "text-yellow-600 dark:text-yellow-400",
      info: "text-blue-600 dark:text-blue-400",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

export interface MessageProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof messageVariants> {
  className?: string;
}

const Message = React.forwardRef<HTMLDivElement, MessageProps>(
  ({ className, variant, children, ...props }, ref) => {
    const Icon = icons[variant || "info"];

    return (
      <div
        ref={ref}
        className={cn(messageVariants({ variant }), className)}
        role="alert"
        {...props}
      >
        <Icon className={cn(iconVariants({ variant }))} />
        <div className="flex-1">{children}</div>
      </div>
    );
  },
);

Message.displayName = "Message";

export { Message, messageVariants };
